import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { TitleAreaContainer } from "./styles";
import { Heading } from "../../../styles";
import SiteAPIClient from "../../../api/clients/site";

// In-memory cache for titles options across component instances
let cachedTitlesOptions = null;
let titlesOptionsPromise = null;

export const fetchTitlesOptions = async () => {
  if (cachedTitlesOptions) return cachedTitlesOptions;
  if (titlesOptionsPromise) return titlesOptionsPromise;

  titlesOptionsPromise = (async () => {
    try {
      const siteClient = new SiteAPIClient();
      const res = await siteClient.getTitlesOptions();
      if (res && res.success && res.options) {
        cachedTitlesOptions = res.options;
        return res.options;
      }
    } catch (err) {
      console.error("Failed to load titles options:", err);
    } finally {
      titlesOptionsPromise = null;
    }
    return null;
  })();

  return titlesOptionsPromise;
};

const resolveCurrentArea = (areaProp, pathname = "") => {
  if (areaProp) {
    return areaProp.toLowerCase();
  }

  const cleanPath = (pathname || "").toLowerCase();

  if (cleanPath.startsWith("/post") || cleanPath.startsWith("/blog")) {
    return "posts";
  }

  if (cleanPath.startsWith("/product-group")) {
    return "product_groups";
  }

  if (cleanPath.startsWith("/product")) {
    return "products";
  }

  // Fallback for pages: /page/*, /technical-files, /favorites, /quote, /results/*, etc.
  return "pages";
};

const shouldApplyOptions = (options, currentArea) => {
  if (!options) return false;

  const target = options.target || "global";

  if (target === "global") {
    return true;
  }

  if (target === "specific_areas") {
    const selectedAreas = Array.isArray(options.areas)
      ? options.areas.map((a) => a.toLowerCase())
      : [];

    switch (currentArea) {
      case "posts":
        return selectedAreas.includes("posts");

      case "pages":
        return selectedAreas.includes("pages");

      case "products":
        return selectedAreas.includes("products");

      case "product_groups":
      case "productgroups":
        return (
          selectedAreas.includes("product_groups") ||
          selectedAreas.includes("productgroups")
        );

      default:
        return selectedAreas.includes(currentArea);
    }
  }

  return false;
};

const mapTextCaseToTransform = (textCase) => {
  if (!textCase) return undefined;
  switch (textCase.toLowerCase()) {
    case "uppercase":
      return "uppercase";
    case "lowercase":
      return "lowercase";
    case "capitalize":
      return "capitalize";
    case "initial":
    case "original":
      return "none";
    default:
      return textCase;
  }
};

export default function TitleArea({ title, tTransform, area }) {
  const [titlesOptions, setTitlesOptions] = useState(cachedTitlesOptions);
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;
    fetchTitlesOptions().then((options) => {
      if (isMounted && options) {
        setTitlesOptions(options);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  if (!title || !title.title) return null;

  const currentArea = resolveCurrentArea(area, location?.pathname);
  const applyOptions = shouldApplyOptions(titlesOptions, currentArea);

  const effectiveTransform =
    applyOptions && titlesOptions?.text_case
      ? mapTextCaseToTransform(titlesOptions.text_case)
      : tTransform || "uppercase";

  const disableShadow = applyOptions
    ? Boolean(titlesOptions?.disable_text_drop_shadow)
    : false;

  return (
    <TitleAreaContainer
      backgroundImage={title.image}
      disableShadow={disableShadow}
    >
      <Heading type={"h1"} tTransform={effectiveTransform} fontSize="9em">
        {title.title}
      </Heading>
      {/** we add a dark overlay */}
      <div className="bg-black opacity-50 w-full h-full absolute top-0 left-0"></div>
    </TitleAreaContainer>
  );
}
